"use client";
import clsx from "clsx";
import { forwardRef, useId } from "react";
import ErrorMessage from "../ErrorMessage";
import InfoIcon from "../InfoIcon";
import styles from "./TextField.module.css";
import { TextFieldProps } from "./types";

const TextField = forwardRef<
  HTMLInputElement & HTMLTextAreaElement,
  TextFieldProps
>(function TextField(
  {
    textarea = false,
    thousandSeparatorEnabled,
    thousandSeparatorLocale = "de-DE",
    label,
    errorMessage,
    type = "text",
    inverted,
    onChange,
    onNumberChange,
    className,
    variant,
    suffix,
    prefix,
    size,
    disabled,
    info,
    hint,
    infoPlacement,
    as,
    fitContent = false,
    ...props
  },
  ref,
) {
  const genId = useId();
  const id = props.id || genId;
  size = size || "md";

  const shared = {
    container: clsx("border transition-colors ease-out flex items-center", {
      // use 16px on mobile to prevent automatic zooming by safari
      "text-button-1 m:text-body-2 rounded-s": size === "md",
      "text-app-button-1 m:text-app-body-2 rounded-xs": size === "sm",
      "cursor-not-allowed": disabled,
      "border-neutral-425 hover:border-neutral-425 text-neutral-675":
        disabled && inverted,
      "border-neutral-50 hover:border-neutral-50 text-neutral-150":
        disabled && !inverted,
    }),
    input: clsx(
      "w-full peer resize-y focus:outline-none disabled:outline-none disabled:cursor-not-allowed text-inherit bg-[inherit]",
      {
        "field-sizing-content": fitContent,
        "px-s py-[11px] rounded-s": size === "md",
        "px-3xs py-[7px] rounded-xs": size === "sm",
      },
    ),
  };

  const variants = {
    normal: {
      title: "text-neutral-675",
      container: clsx(
        "border-neutral-50 text-neutral-575 hover:border-neutral-100 focus-within:border-neutral-100",
        {
          "bg-neutral-0": !disabled,
          "bg-neutral-12": disabled,
        },
      ),
      input: "placeholder:text-neutral-150",
      suffix: "text-neutral-150",
    },
    inverted: {
      title: "text-neutral-0",
      container:
        "bg-cadet-900 border-neutral-425 text-neutral-0 hover:border-neutral-100 focus-within:border-neutral-100",
      input: "placeholder:text-neutral-150 bg-cadet-900",
      suffix: "",
    },
    primary: {
      title: "text-neutral-0",
      container:
        "bg-primary-800 border-neutral-0 text-neutral-0 focus-within:border-neutral-100",
      input:
        "placeholder:text-neutral-0/60 placeholder:text-body-2 bg-primary-800 hover:bg-primary-850 transition-all",
      suffix: "",
    },
    cadet: {
      title: "text-neutral-0",
      container:
        "bg-cadet-850 border-neutral-0 text-neutral-0 focus-within:border-neutral-100",
      input:
        "placeholder:text-neutral-0/60 placeholder:text-body-2 bg-cadet-850 hover:bg-cadet-900 transition-all",
      suffix: "",
    },
    neutral: {
      title: "text-neutral-675",
      container:
        "bg-neutral-12 border-neutral-12 text-neutral-675 focus-within:border-neutral-100",
      input: "placeholder:text-neutral-575/60",
      suffix: "",
    },
  };

  const variantClasses = inverted
    ? variants.inverted
    : variant
      ? variants[variant]
      : variants.normal;

  const Input = as || "input";

  return (
    <div id={`${id}-container`} className={clsx("w-full", className)}>
      {label && (
        <label
          htmlFor={id}
          className={clsx(
            "flex items-center gap-2xs mb-xs",
            {
              "text-title-1": size === "md",
              "text-app-title-1": size === "sm",
            },
            variantClasses.title,
          )}
        >
          {label}
          {!!info && <InfoIcon placement={infoPlacement}>{info}</InfoIcon>}
        </label>
      )}
      <div className={clsx(shared.container, variantClasses.container)}>
        {prefix && (
          <div
            className={clsx(
              "transition-all ml-s flex items-center",
              variantClasses.suffix,
              {
                "text-error-700": errorMessage,
              },
            )}
          >
            {prefix}
          </div>
        )}
        {textarea ? (
          <textarea
            aria-invalid={!!errorMessage}
            id={id}
            ref={ref}
            {...props}
            onChange={onChange}
            className={clsx(shared.input, variantClasses.input)}
          />
        ) : (
          <Input
            aria-invalid={!!errorMessage}
            id={id}
            type={thousandSeparatorEnabled ? "text" : type}
            ref={ref}
            disabled={disabled}
            {...props}
            className={clsx(shared.input, variantClasses.input, styles.input)}
            onChange={(e) => {
              if (thousandSeparatorEnabled) {
                const pristine = e.target.value.replace(/[^\d]/g, "");
                if (pristine.length > 0) {
                  e.target.value = parseInt(pristine).toLocaleString(
                    thousandSeparatorLocale,
                  );
                  onNumberChange?.(parseInt(pristine));
                } else {
                  e.target.value = "";
                  onNumberChange?.(0);
                }
              } else {
                if (e.target.value) {
                  onNumberChange?.(parseInt(e.target.value));
                } else {
                  onNumberChange?.(0);
                }
              }
              onChange?.(e);
            }}
          />
        )}
        {suffix && (
          <div
            className={clsx(
              "transition-all flex items-center",
              variantClasses.suffix,
              {
                "text-error-700": errorMessage,
                "mr-s": size === "md",
                "mr-3xs": size === "sm",
              },
            )}
          >
            {suffix}
          </div>
        )}
      </div>

      {hint && <p className="text-title-1 text-neutral-675/60 mt-xs">{hint}</p>}
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </div>
  );
});

export default TextField;
